import React, { useEffect, useState } from 'react';
import {
    listTrashedProspects, restoreProspect, hardDeleteProspect,
    listTrashedDossiers, restoreDossier, hardDeleteDossier,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Trash2, RotateCcw, AlertCircle, Loader2, Users, FolderKanban } from 'lucide-react';
import { toast } from 'sonner';

const formatDate = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
};

const prospectLabel = (p) => {
    if (p.societe) return p.societe;
    const n = `${p.prenom || ''} ${p.nom || ''}`.trim();
    return n || `Prospect ${p.id.slice(-6)}`;
};

const dossierLabel = (d) => d.titre || `Dossier ${d.id.slice(-6)}`;

export default function CorbeillePage() {
    const [tab, setTab] = useState('prospects'); // 'prospects' | 'dossiers'
    const [prospects, setProspects] = useState([]);
    const [dossiers, setDossiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [retention, setRetention] = useState(30);
    const [hardTarget, setHardTarget] = useState(null); // { kind, item }
    const [busy, setBusy] = useState(false);

    const loadProspects = async (silent = false) => {
        try {
            const res = await listTrashedProspects();
            setProspects(res.data.items || []);
            setRetention(res.data.retention_days || 30);
            const purged = res.data.purged_this_call || 0;
            if (!silent && purged > 0) {
                toast.info(`${purged} prospect(s) purgé(s) définitivement (rétention dépassée)`);
            }
        } catch (err) {
            if (!silent) toast.error(err?.response?.data?.detail || 'Erreur de chargement (prospects)');
        }
    };

    const loadDossiers = async (silent = false) => {
        try {
            const res = await listTrashedDossiers();
            setDossiers(res.data.items || []);
            setRetention((prev) => res.data.retention_days || prev || 30);
            const purged = res.data.purged_this_call || 0;
            if (!silent && purged > 0) {
                toast.info(`${purged} dossier(s) purgé(s) définitivement (rétention dépassée)`);
            }
        } catch (err) {
            if (!silent) toast.error(err?.response?.data?.detail || 'Erreur de chargement (dossiers)');
        }
    };

    const loadAll = async () => {
        setLoading(true);
        await Promise.all([loadProspects(), loadDossiers()]);
        setLoading(false);
    };

    useEffect(() => { loadAll(); }, []);

    const handleRestoreProspect = async (id, label) => {
        setBusy(true);
        try {
            await restoreProspect(id);
            toast.success(`${label} restauré`);
            setProspects((prev) => prev.filter((p) => p.id !== id));
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur');
        } finally { setBusy(false); }
    };

    const handleRestoreDossier = async (id, label) => {
        setBusy(true);
        try {
            await restoreDossier(id);
            toast.success(`${label} restauré`);
            setDossiers((prev) => prev.filter((d) => d.id !== id));
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur');
        } finally { setBusy(false); }
    };

    const handleHardDelete = async () => {
        if (!hardTarget) return;
        setBusy(true);
        try {
            if (hardTarget.kind === 'prospect') {
                await hardDeleteProspect(hardTarget.item.id);
                toast.success(`${prospectLabel(hardTarget.item)} supprimé définitivement`);
                setProspects((prev) => prev.filter((p) => p.id !== hardTarget.item.id));
            } else {
                await hardDeleteDossier(hardTarget.item.id);
                toast.success(`${dossierLabel(hardTarget.item)} supprimé définitivement`);
                setDossiers((prev) => prev.filter((d) => d.id !== hardTarget.item.id));
            }
            setHardTarget(null);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur');
        } finally { setBusy(false); }
    };

    const items = tab === 'prospects' ? prospects : dossiers;

    return (
        <div className="max-w-6xl mx-auto p-4 lg:p-6 space-y-6" data-testid="corbeille-page">
            <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-[#0A0A0A] tracking-tight flex items-center gap-3">
                    <Trash2 className="w-7 h-7 text-[#DC2626]" />
                    Corbeille
                </h1>
                <p className="text-sm text-[#52525B] mt-1">
                    Fiches supprimées récemment. Elles sont automatiquement supprimées définitivement après <strong>{retention} jours</strong>.
                </p>
            </div>

            <div className="bg-[#B45309]/5 border border-[#B45309]/30 rounded-sm p-3 text-xs text-[#B45309] flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                    <strong>Réservé aux administrateurs.</strong> La restauration remet la fiche à sa place précédente avec toutes ses données. La suppression définitive est irréversible et purge également les documents, coûts et factures rattachés au dossier.
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-[#E4E4E7]" role="tablist">
                <button
                    role="tab"
                    aria-selected={tab === 'prospects'}
                    onClick={() => setTab('prospects')}
                    className={`px-4 py-2 text-sm font-semibold flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                        tab === 'prospects'
                            ? 'border-[#0A0A0A] text-[#0A0A0A]'
                            : 'border-transparent text-[#71717A] hover:text-[#0A0A0A]'
                    }`}
                    data-testid="corbeille-tab-prospects"
                >
                    <Users className="w-4 h-4" />
                    Prospects
                    <span className="text-xs bg-[#F4F4F5] text-[#52525B] px-2 py-0.5 rounded-full">
                        {prospects.length}
                    </span>
                </button>
                <button
                    role="tab"
                    aria-selected={tab === 'dossiers'}
                    onClick={() => setTab('dossiers')}
                    className={`px-4 py-2 text-sm font-semibold flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                        tab === 'dossiers'
                            ? 'border-[#0A0A0A] text-[#0A0A0A]'
                            : 'border-transparent text-[#71717A] hover:text-[#0A0A0A]'
                    }`}
                    data-testid="corbeille-tab-dossiers"
                >
                    <FolderKanban className="w-4 h-4" />
                    Dossiers
                    <span className="text-xs bg-[#F4F4F5] text-[#52525B] px-2 py-0.5 rounded-full">
                        {dossiers.length}
                    </span>
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16 text-[#71717A]">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…
                </div>
            ) : items.length === 0 ? (
                <Card className="p-12 text-center text-[#71717A] rounded-sm" data-testid={`corbeille-empty-${tab}`}>
                    <Trash2 className="w-12 h-12 mx-auto mb-3 text-[#D4D4D8]" />
                    <p className="font-semibold text-[#0A0A0A]">La corbeille est vide</p>
                    <p className="text-sm mt-1">
                        Les {tab === 'prospects' ? 'prospects' : 'dossiers'} supprimés apparaîtront ici pendant {retention} jours.
                    </p>
                </Card>
            ) : tab === 'prospects' ? (
                <Card className="rounded-sm overflow-hidden">
                    <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[720px]">
                        <thead className="bg-[#F4F4F5]">
                            <tr>
                                <th className="px-4 py-2 text-left font-semibold text-[#52525B]">Prospect</th>
                                <th className="px-4 py-2 text-left font-semibold text-[#52525B]">Type</th>
                                <th className="px-4 py-2 text-left font-semibold text-[#52525B]">Supprimé le</th>
                                <th className="px-4 py-2 text-left font-semibold text-[#52525B]">Jours restants</th>
                                <th className="px-4 py-2 text-right font-semibold text-[#52525B] w-64">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#E4E4E7]">
                            {prospects.map((p) => {
                                const label = prospectLabel(p);
                                const daysRem = p.days_remaining;
                                const urgent = daysRem !== null && daysRem <= 3;
                                return (
                                    <tr key={p.id} className="hover:bg-[#FAFAFA]" data-testid={`corbeille-row-${p.id}`}>
                                        <td className="px-4 py-3">
                                            <div className="font-semibold text-[#0A0A0A]">{label}</div>
                                            <div className="text-[11px] text-[#71717A]">{p.email || p.telephone || ''}</div>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-[#52525B] capitalize">{p.type_contact || 'particulier'}</td>
                                        <td className="px-4 py-3 text-xs text-[#52525B]">{formatDate(p.deleted_at)}</td>
                                        <td className={`px-4 py-3 text-xs font-semibold ${urgent ? 'text-[#DC2626]' : 'text-[#0A0A0A]'}`}>
                                            {daysRem === null ? '—' : `${daysRem} j`}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex justify-end gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleRestoreProspect(p.id, label)}
                                                    disabled={busy}
                                                    className="rounded-sm"
                                                    data-testid={`corbeille-restore-btn-${p.id}`}
                                                >
                                                    <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restaurer
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setHardTarget({ kind: 'prospect', item: p })}
                                                    disabled={busy}
                                                    className="rounded-sm text-[#DC2626] hover:bg-[#DC2626]/5 border-[#DC2626]/30"
                                                    data-testid={`corbeille-hard-btn-${p.id}`}
                                                >
                                                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer définitivement
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    </div>
                </Card>
            ) : (
                <Card className="rounded-sm overflow-hidden">
                    <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[720px]">
                        <thead className="bg-[#F4F4F5]">
                            <tr>
                                <th className="px-4 py-2 text-left font-semibold text-[#52525B]">Dossier</th>
                                <th className="px-4 py-2 text-left font-semibold text-[#52525B]">Client</th>
                                <th className="px-4 py-2 text-left font-semibold text-[#52525B]">Supprimé le</th>
                                <th className="px-4 py-2 text-left font-semibold text-[#52525B]">Jours restants</th>
                                <th className="px-4 py-2 text-right font-semibold text-[#52525B] w-64">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#E4E4E7]">
                            {dossiers.map((d) => {
                                const label = dossierLabel(d);
                                const daysRem = d.days_remaining;
                                const urgent = daysRem !== null && daysRem <= 3;
                                return (
                                    <tr key={d.id} className="hover:bg-[#FAFAFA]" data-testid={`corbeille-dossier-row-${d.id}`}>
                                        <td className="px-4 py-3">
                                            <div className="font-semibold text-[#0A0A0A]">{label}</div>
                                            <div className="text-[11px] text-[#71717A]">{d.type_travaux || ''}</div>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-[#52525B]">{d.prospect_nom || '—'}</td>
                                        <td className="px-4 py-3 text-xs text-[#52525B]">{formatDate(d.deleted_at)}</td>
                                        <td className={`px-4 py-3 text-xs font-semibold ${urgent ? 'text-[#DC2626]' : 'text-[#0A0A0A]'}`}>
                                            {daysRem === null ? '—' : `${daysRem} j`}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex justify-end gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleRestoreDossier(d.id, label)}
                                                    disabled={busy}
                                                    className="rounded-sm"
                                                    data-testid={`corbeille-dossier-restore-btn-${d.id}`}
                                                >
                                                    <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restaurer
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setHardTarget({ kind: 'dossier', item: d })}
                                                    disabled={busy}
                                                    className="rounded-sm text-[#DC2626] hover:bg-[#DC2626]/5 border-[#DC2626]/30"
                                                    data-testid={`corbeille-dossier-hard-btn-${d.id}`}
                                                >
                                                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer définitivement
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    </div>
                </Card>
            )}

            <AlertDialog open={!!hardTarget} onOpenChange={(open) => { if (!open && !busy) setHardTarget(null); }}>
                <AlertDialogContent data-testid="corbeille-hard-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Trash2 className="w-5 h-5 text-[#DC2626]" />
                            Suppression définitive ?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            <span className="font-semibold text-[#0A0A0A]">
                                {hardTarget
                                    ? hardTarget.kind === 'prospect'
                                        ? prospectLabel(hardTarget.item)
                                        : dossierLabel(hardTarget.item)
                                    : ''}
                            </span> sera supprimé de la base de données
                            {hardTarget?.kind === 'dossier' ? ' avec tous ses documents, coûts et factures rattachés' : ''}.
                            Cette action est <span className="font-semibold text-[#DC2626]">irréversible</span> et ne pourra
                            plus être annulée.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={busy}>Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleHardDelete}
                            disabled={busy}
                            className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                            data-testid="corbeille-hard-confirm"
                        >
                            {busy ? 'Suppression…' : 'Supprimer définitivement'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
